import './App.css';
import {
  Routes,
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';
import React, { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { HelmetProvider } from 'react-helmet-async';
import { isTeacherSubdomain, getCrossDomainUrl, isStudentRoute } from './utils/subdomainDetector';
import StripeNavbar from "./StripeNavbar";
import StripeHomepage from "./StripeHomepage";
import SignIn from './SignIn';
import SignUp from './SignUp';
import ComingSoon from './ComingSoon';
import TeacherDashboardLayout from './TeacherDashboardLayout';
import TeacherDashboard from './TeacherDashboard';
import OrdersPage from './Orders';
import ChatPage from './Chat';
import CalendarPage from './Calendar';
import TeacherProfile from './TeacherProfile';
import StudentDashboardLayout from './StudentDashboardLayout';
import StudentDashboard from './StudentDashboard';
import TeacherBrowsePage from './TeacherBrowsePage';
import StudentOnboarding from './StudentOnboarding';
import InstitutesPage from './Institutes';
import HelpCenter from './HelpCenter';
import StudentInstituteSessions from './StudentInstituteSessions';
import RateTeacher from './RateTeacher';
import InstituteDashboard from './InstituteDashboard';
import ProfilePage from './Profile';
import About from './About';
import AboutArabic from './AboutArabic';
import ResetPassword from './ResetPassword';
import RequestsPage from './RequestsPage';
import Terms from './Terms';
import TermsArabic from './TermsArabic';
import Privacy from './Privacy';
import PrivacyArabic from './PrivacyArabic';
import FAQ from './FAQ';
import TeacherFAQ from './TeacherFAQ';
import TeacherPricing from './TeacherPricing';
import PublicHelpCenter from './PublicHelpCenter';
import TeacherConfirmation from './TeacherConfirmation';
import AdminDashboardLayout from './AdminDashboardLayout';
import AdminDashboard from './AdminDashboard';
import AdminUsersPage from './AdminUsersPage';
import AdminBookingsPage from './AdminBookingsPage';
import AdminSessionsPage from './AdminSessionsPage';
import AdminPaymentsPage from './AdminPaymentsPage';
import AdminRewardsPage from './AdminRewardsPage';
import AdminSecurityPage from './AdminSecurityPage';
import AdminProfilePage from './AdminProfilePage';
import AdminChatPage from './AdminChatPage';
import XPHistoryPage from './components/gamification/XPHistoryPage';
import TeacherLeaderboardComingSoon from './TeacherLeaderboardComingSoon';
import NetworkStatus from './components/NetworkStatus';
import NotFound from './NotFound';
import PaymentPage from './PaymentPage';
import PaymentCallbackPage from './PaymentCallbackPage';
import TeacherPayoutPage from './TeacherPayoutPage';
import TeacherDetailPage from './TeacherDetailPage';
import TeacherSignupWizard from './TeacherSignupWizard';

function App() {
  const location = useLocation();
  const isSignIn = location.pathname === "/signin" || location.pathname === "/signup" || location.pathname === "/teacher-confirm";
  const isDashboard = location.pathname.startsWith("/dashboard/");
  const isLegal = location.pathname.startsWith("/terms") || location.pathname.startsWith("/privacy");
  const isInstitute = location.pathname === "/institute";
  const isHomepage = location.pathname === "/";

  // Helper functions for cross-subdomain language persistence using cookies
  const getLanguageFromCookie = () => {
    const match = document.cookie.match(/taealam_language=([^;]+)/);
    return match ? match[1] : 'en';
  };

  const setLanguageCookie = (lang) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname.includes('localhost');
    if (isLocalhost) {
      document.cookie = `taealam_language=${lang}; path=/; max-age=31536000`;
    } else {
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = `taealam_language=${lang}; path=/; domain=.${domain}; max-age=31536000`;
    }
  };

  const [lang, setLang] = React.useState(() => {
    const cookieLang = getLanguageFromCookie();
    if (cookieLang) return cookieLang;

    const savedLang = localStorage.getItem('taealam_language');
    if (savedLang) {
      setLanguageCookie(savedLang);
      return savedLang;
    }

    return 'en';
  });

  const onTeacherSubdomain = isTeacherSubdomain();
  const onMainSubdomain = !onTeacherSubdomain;

  useEffect(() => {
    setLanguageCookie(lang);
    localStorage.setItem('taealam_language', lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const pathname = location.pathname;
    const sharedRoutes = ['/signin', '/signup', '/reset-password', '/terms', '/privacy', '/terms-ar', '/privacy-ar'];
    if (sharedRoutes.some(route => pathname.startsWith(route))) {
      return;
    }

    if (onTeacherSubdomain && isStudentRoute(pathname)) {
      window.location.href = getCrossDomainUrl('student', pathname);
      return;
    }

    if (onMainSubdomain && pathname.startsWith('/dashboard/teacher')) {
      window.location.href = getCrossDomainUrl('teacher', pathname);
      return;
    }
  }, [location.pathname, onTeacherSubdomain, onMainSubdomain]);

  return (
    <HelmetProvider>
      {/* Network status banner - shown on all pages */}
      <NetworkStatus />

      {/* Stripe Navbar only on homepage */}
      {isHomepage && <StripeNavbar lang={lang} setLang={setLang} />}

      <Routes>
        {/* Shared Routes - Available on BOTH subdomains */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms-ar" element={<TermsArabic />} />
        <Route path="/privacy-ar" element={<PrivacyArabic />} />
        <Route path="/coming-soon" element={<ComingSoon />} />

        {/* Home page - New Stripe-style design */}
        <Route path="/" element={<StripeHomepage lang={lang} isStudent={onMainSubdomain} />} />

        {/* Teacher confirmation - Only available on main subdomain (not teacher subdomain) */}
        <Route 
          path="/teacher-confirm" 
          element={onMainSubdomain ? <TeacherConfirmation /> : <NotFound isArabic={lang === 'ar'} />} 
        />

        {/* Teacher Signup Wizard */}
        <Route path="/teacher/signup" element={<TeacherSignupWizard />} />

        {/* Teacher Dashboard - works on both subdomains */}
        <Route path="/dashboard/teacher" element={<TeacherDashboardLayout />}>
          <Route path="dashboard" element={<TeacherDashboard />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="leaderboard" element={<TeacherLeaderboardComingSoon />} />
          <Route path="help-center" element={<HelpCenter />} />
          <Route path="profile" element={<TeacherProfile />} />
          <Route path="payouts" element={<TeacherPayoutPage />} />
        </Route>

        {/* Public pages */}
        <Route path="/about" element={<About />} />
        <Route path="/about-ar" element={<AboutArabic />} />
        <Route path="/faq" element={
          (() => {
            const isTeacher = isTeacherSubdomain();
            return isTeacher ? <TeacherFAQ /> : <FAQ />;
          })()
        } />
        <Route path="/pricing" element={
          (() => {
            const isTeacher = isTeacherSubdomain();
            return isTeacher ? <TeacherPricing /> : <NotFound isArabic={lang === 'ar'} />;
          })()
        } />
        <Route path="/help-center" element={<PublicHelpCenter />} />

        {/* Teacher Detail Page */}
        <Route path="/teacher/:uuid" element={<TeacherDetailPage />} />

        {/* Student Dashboard */}
        <Route path="/dashboard/student/onboarding" element={<StudentOnboarding lang={lang} />} />
        <Route path="/dashboard/student/rate" element={<RateTeacher />} />
        <Route path="/dashboard/student" element={<StudentDashboardLayout />}>
          <Route path="dashboard" element={<StudentDashboard />} />
          <Route path="teachers" element={<TeacherBrowsePage />} />
          <Route path="institutes" element={<InstitutesPage />} />
          <Route path="help-center" element={<HelpCenter />} />
          <Route path="institute-sessions" element={<StudentInstituteSessions />} />
          <Route path="requests" element={<OrdersPage />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="xp" element={<XPHistoryPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>

        {/* Admin Dashboard */}
        <Route path="/dashboard/admin" element={<AdminDashboardLayout />}>
          <Route path="overview" element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="bookings" element={<AdminBookingsPage />} />
          <Route path="sessions" element={<AdminSessionsPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="rewards" element={<AdminRewardsPage />} />
          <Route path="security" element={<AdminSecurityPage />} />
          <Route path="chat" element={<AdminChatPage />} />
          <Route path="help-center" element={<HelpCenter />} />
          <Route path="profile" element={<AdminProfilePage />} />
        </Route>

        {/* Institute */}
        <Route path="/institute" element={<InstituteDashboard />} />

        {/* Payment Routes */}
        <Route path="/payment/:bookingId" element={<PaymentPage />} />
        <Route path="/payment/callback" element={<PaymentCallbackPage />} />
        <Route path="/payment/success/:bookingId" element={<PaymentCallbackPage />} />

        {/* 404 Not Found - Catch all unmatched routes */}
        <Route path="*" element={<NotFound isArabic={lang === 'ar'} />} />
      </Routes>
      <Analytics />
      <SpeedInsights />
    </HelmetProvider>
  );
}

export default App;

